/**
 * Pressure control BOP-2 (api#1281) — the pieces every pressure-control list
 * shares: the section strip (Components · Tests · Drills · Gauges), a saturated
 * badge, and the equipment / people pickers' option lists.
 *
 * THE STRIP IS THE WAY IN. The module has one sidebar entry (the register, on
 * a Well Servicing tenant) and a band button on /equipment; tests, drills and
 * gauges are reached from this strip, which sits in the body directly under
 * the band (CLAUDE.md, tab strips). It switches WHICH LIST the page shows, so
 * it carries no counts. The active section is marked by the amber underline
 * and the weight, never by ink colour.
 */
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../api/client';
import { unwrapArray } from '../../lib/api-envelope';
import { PC_CALIBRATIONS, PC_DRILLS, PC_LIST, PC_TESTS } from '../../lib/pressure-control';

const SECTIONS = [
  { to: PC_LIST, label: 'Components' },
  { to: PC_TESTS, label: 'Pressure tests' },
  { to: PC_DRILLS, label: 'Crew drills' },
  { to: PC_CALIBRATIONS, label: 'Gauges' },
] as const;

/** The section whose path is the longest prefix of `pathname`. */
export function pcSectionFor(pathname: string): string {
  const hit = [...SECTIONS].sort((a, b) => b.to.length - a.to.length).find((s) => pathname === s.to || pathname.startsWith(`${s.to}/`));
  return hit?.to ?? PC_LIST;
}

export function PcSectionNav() {
  const { pathname } = useLocation();
  const active = pcSectionFor(pathname);
  return (
    <nav aria-label="Pressure control sections" className="overflow-x-auto" data-testid="pc-section-nav">
      <div className="flex min-w-max items-center gap-1 border-b border-fieldiq-border">
        {SECTIONS.map((s) => {
          const on = s.to === active;
          return (
            <Link
              key={s.to}
              to={s.to}
              aria-current={on ? 'page' : undefined}
              className={`-mb-px whitespace-nowrap border-b-2 px-4 py-2 text-sm transition-colors ${
                on ? 'border-fi-amber font-extrabold text-fieldiq-text' : 'border-transparent font-medium text-fieldiq-muted hover:text-fieldiq-text'
              }`}
            >
              {s.label}
            </Link>
          );
        })}
      </div>
    </nav>
  );
}

/** Saturated fill, dark ink. A value the maps do not know reads `—` on slate. */
export function PcBadge({
  value,
  labels,
  fills,
  testId,
}: {
  value: string | null | undefined;
  labels: Record<string, string>;
  fills: Record<string, string>;
  testId?: string;
}) {
  const label = (value && labels[value]) || '—';
  const fill = (value && fills[value]) || 'bg-fi-signal-slate';
  return (
    <span
      data-testid={testId}
      data-value={value ?? ''}
      className={`inline-flex w-fit whitespace-nowrap rounded-fi-pill border border-black/20 px-2.5 py-1 text-[0.68rem] font-black uppercase leading-none tracking-[0.06em] text-fi-signal-ink ${fill}`}
    >
      {label}
    </span>
  );
}

export interface PcOption {
  id: string;
  name: string;
}

/** Live equipment rows as picker options (a gauge, a rig). Fetched only when `enabled`. */
export function usePcEquipmentOptions(enabled: boolean) {
  return useQuery({
    queryKey: ['equipment', 'list', 'pc-options'],
    queryFn: async (): Promise<PcOption[]> =>
      unwrapArray<any>((await api.equipment.list()).data)
        .filter((e) => e?.id)
        .map((e) => {
          const unit = e.unitNumber ?? e.unit_number;
          return { id: String(e.id), name: `${e.name ?? '—'}${unit ? ` · Unit ${unit}` : ''}` };
        })
        .sort((a, b) => a.name.localeCompare(b.name)),
    enabled,
  });
}

/** The company's people (not terminated) as picker options. Fetched only when `enabled`. */
export function usePcPeopleOptions(enabled: boolean) {
  return useQuery({
    queryKey: ['users', 'list', 'pc-options'],
    queryFn: async (): Promise<PcOption[]> =>
      unwrapArray<any>((await api.users.list()).data)
        .filter((u) => u?.id && (u.employmentStatus ?? u.employment_status) !== 'terminated')
        .map((u) => {
          const first = u.firstName ?? u.first_name ?? '';
          const last = u.lastName ?? u.last_name ?? '';
          return { id: String(u.id), name: `${first} ${last}`.trim() || u.email || '—' };
        })
        .sort((a, b) => a.name.localeCompare(b.name)),
    enabled,
  });
}

/** A red-washed error line inside a dialog or form. */
export function PcErrorLine({ message, children }: { message: string | null; children?: React.ReactNode }) {
  if (!message) return null;
  return (
    <div role="alert" className="mt-4 rounded-lg border border-fi-signal-red/40 bg-fi-signal-red/10 px-3 py-2 text-sm text-fieldiq-text">
      <p>{message}</p>
      {children}
    </div>
  );
}
